import { useRef, useState } from 'react';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { Maybe } from 'shared/utils/types';
import { CombinedError } from 'urql';

/**
 *
 * [RFC [3] Cruise Idempotency Token Compliant](https://varomoney.atlassian.net/wiki/spaces/Cruise/pages/3315859513/RFC+3+-+Cruise+Idempotency+Tokens) Utils
 * 1. Clients MUST generate a new idempotency at the beginning of an expressed user intent.
 * 2. Clients MUST rotate the idempotency id if they receive a known error response from the server.
 * 3. Clients SHOULD NOT rotate the idempotency id until the submission UI has been removed from the screen.
 * 4. Clients MUST NOT rotate the idempotency id unless they receive a valid response from the server
 * 5. Clients MUST regenerate the idempotency token for each subsequent start of that expressed user intent.
 */

/**
 *
 * @param seed changing the seed value automatically rotates the idempotency token.
 *  you can pass a value in a query param through a flow to indicate a new user intent
 *  has started and the idempotency token should be rotated.
 * @returns an idempotency token and a helper to rotate the idempotency token.
 */
export const useIdempotencyToken = (seed: number | string) => {
  const [idempotencyToken, setIdempotencyToken] = useState<string>(getUuid());
  const seedRef = useRef(seed);

  const rotateToken = () => {
    setIdempotencyToken(getUuid());
  };

  // if the seed value
  if (seedRef.current !== seed) {
    seedRef.current = seed;
    rotateToken();
  }

  return { idempotencyToken, rotateToken };
};

export type IdempotencyTokenForMutationProps<DataType> = {
  seed: number | string;
  data: Maybe<DataType>;
  error: Maybe<CombinedError>;
  autoRotateToken?: Maybe<
    (data: DataType, error: Maybe<CombinedError>) => boolean
  >;
};
/**
 * provides an api for working with idempotency tokens using urql mutations.
 * `seed`: rotate this to change the idempotency token based on user intent. ie. a new deposit.
 * `data`: the data returned from the mutation. when this changes you may chose to auto rotate the token.
 * `error`: if the error includes network errors this hook will not rotate the token because the server state is unknown.
 * `autoRotateToken`: a function that can be used to automatically rotate the token in response to
 *   a successful request or a known partial success. by default auto rotation is disabled and you can call `rotateToken` manually.
 *   consumers may provide a mapping of validation errors or other partially successful responses to automatically
 *   rotate when known conditions are met that should enable retry to be compliant with this guideline:
 *   > Clients MUST rotate the idempotency id if they receive a known error response from the server.
 *
 * @param args: see discussion above
 */
export function useIdempotencyTokenForMutation<DataType>({
  seed,
  data,
  error,
  autoRotateToken = () => false,
}: IdempotencyTokenForMutationProps<DataType>) {
  const { idempotencyToken, rotateToken } = useIdempotencyToken(seed);

  const dataRef = useRef(data);
  const errorRef = useRef(error);

  if (error !== errorRef.current) {
    errorRef.current = error;

    // if there was a network error, don't rotate the token since the server state is unknown
    if (error && error.networkError) {
      return { idempotencyToken, rotateToken };
    }
  }

  // if the data has changed and is satisfies requirements, rotate the token
  if (data !== dataRef.current) {
    dataRef.current = data;
    if (data && autoRotateToken?.(data, error)) {
      rotateToken();
    }
  }

  return { idempotencyToken, rotateToken };
}
